import { useState, useEffect, useRef } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { applySorting } from '../../utils/sortingUtils';

/**
 * SortMenu Component
 * 
 * A reusable sort menu component that provides sorting functionality
 * with dropdown interface and various sorting options.
 * 
 * Props:
 * - onSortChange: Callback function called when sort option changes
 * - className: Additional CSS classes for styling
 * 
 * Expected data structure for each item:
 * {
 *   amazonIndex: number,     // Original index from Amazon results (0-based) - REQUIRED for accurate sorting
 *   weight: number,          // Legacy weight property (optional)
 *   locales: {
 *     [localeCode]: {
 *       priceIls: number,    // Price in ILS for this locale
 *       // ... other locale-specific data
 *     }
 *   }
 * }
 * 
 * Note: The backend should include `amazonIndex` property for each item
 * to preserve the original Amazon search result order.
 */
function SortMenu({ onSortChange, className = '' }) {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [selectedSortOption, setSelectedSortOption] = useState('amazon_results');
  const sortButtonRef = useRef(null);

  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSortMenu && !event.target.closest('.sort-menu-container')) {
        setShowSortMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSortMenu]);

  const toggleSortMenu = () => {
    if (!showSortMenu && sortButtonRef.current) {
      // Calculate position when opening
      const rect = sortButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    
    setShowSortMenu(!showSortMenu);
  };

  const handleSortOption = (option) => {
    setSelectedSortOption(option);
    setShowSortMenu(false);
    
    // Call the callback to notify parent component about sort change
    if (onSortChange) {
      onSortChange(option, applySorting);
    }
  };



  return (
    <div className={`relative sort-menu-container ${className}`}>
      <div 
        ref={sortButtonRef}
        className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
        onClick={toggleSortMenu}
      >
        <span className="text-sm text-gray-700">מיון</span>
        <ArrowUpDown className="w-5 h-5 text-gray-600" />
      </div>
      
      {/* Sort Dropdown Menu */}
      {showSortMenu && (
        <div 
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[160px] z-[9999]"
          style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 9999,
            minWidth: '160px'
          }}
        >
          <div 
            className={`px-4 py-2 text-sm cursor-pointer transition-colors border-b border-gray-100 ${
              selectedSortOption === 'amazon_results' 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleSortOption('amazon_results')}
          >
            <div className="flex items-center justify-between">
              <span>שיכלול אמזון ומספר תוצאות</span>
              {selectedSortOption === 'amazon_results' && (
                <span className="text-blue-600">✓</span>
              )}
            </div>
          </div>
          <div 
            className={`px-4 py-2 text-sm cursor-pointer transition-colors border-b border-gray-100 ${
              selectedSortOption === 'price' 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleSortOption('price')}
          >
            <div className="flex items-center justify-between">
              <span>לפי מחיר</span>
              {selectedSortOption === 'price' && (
                <span className="text-blue-600">✓</span>
              )}
            </div>
          </div>
          <div 
            className={`px-4 py-2 text-sm cursor-pointer transition-colors border-b border-gray-100 ${
              selectedSortOption === 'amazon' 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleSortOption('amazon')}
          >
            <div className="flex items-center justify-between">
              <span>כמו באמזון</span>
              {selectedSortOption === 'amazon' && (
                <span className="text-blue-600">✓</span>
              )}
            </div>
          </div>
          <div 
            className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
              selectedSortOption === 'results' 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => handleSortOption('results')}
          >
            <div className="flex items-center justify-between">
              <span>הכי הרבה תוצאות</span>
              {selectedSortOption === 'results' && (
                <span className="text-blue-600">✓</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SortMenu; 